<template>
  <span class="d-block w-100">
    <span class="d-inline-flex align-items-center">
      <button
        type="button"
        class="btn btn-xs btn-light-primary font-weight-bold py-1 px-2"
        @click="areTasksOpen = !areTasksOpen"
      >
        {{ $t("SERVICE_VISITS.TASKS") }} ({{ totalTaskCount }})
        <v-icon small>
          {{ areTasksOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
      </button>
      <button
        v-if="canEdit"
        type="button"
        class="btn btn-xs btn-light-success font-weight-bold py-1 px-2 ml-1"
        :title="$t('SERVICE_VISITS.ADD_TASK')"
        @click="openCreateTask"
      >
        <v-icon small>mdi-plus</v-icon>
      </button>
    </span>

    <div
      v-if="areTasksOpen"
      class="bg-white border rounded mt-2 w-100 overflow-auto max-h-225px"
    >
      <div
        v-for="(item, index) in tasks"
        :key="item.key"
        class="service-visit-task-row d-flex align-items-center min-w-0 text-dark font-size-sm px-3 py-2"
        :class="{
          'border-bottom': index < tasks.length - 1,
          'cursor-pointer': !canShowReorderHandles,
          'service-visit-task-row--draggable': canReorderTasks,
          'service-visit-task-row--reorder-disabled': canShowReorderHandles && !canReorderTasks,
          'service-visit-task-row--dragging': draggedTaskId === item.id,
          'service-visit-task-row--drop-target': dragOverTaskId === item.id
        }"
        :draggable="canReorderTasks"
        :title="item.name"
        @dragstart="startTaskDrag(item, $event)"
        @dragenter.prevent="markTaskDropTarget(item)"
        @dragover.prevent="markTaskDropTarget(item)"
        @dragleave="clearTaskDropTarget(item)"
        @drop.prevent="dropTask(item)"
        @dragend="finishTaskDrag"
        @click="openTask(item)"
      >
        <v-icon
          v-if="canShowReorderHandles"
          small
          class="service-visit-task-row__handle mr-2"
        >
          mdi-drag
        </v-icon>
        <span class="badge badge-light font-weight-bold mr-2">
          <span
            class="service-visit-task-status-dot d-inline-block rounded-circle mr-1"
            :style="statusDotStyle(item.status)"
          ></span>
          {{ statusName(item.status) }}
        </span>
        <span class="flex-grow-1 min-w-0 text-truncate">
          {{ item.label }}
        </span>
      </div>
      <div v-if="!tasks.length" class="font-size-sm px-3 py-2">
        -
      </div>
    </div>
    <ServiceVisitBoardTaskModal
      :show="isTaskModalOpen"
      :task="selectedTask"
      :mode="modalMode"
      :item-statuses="itemStatuses"
      :can-edit="canEditTask"
      :can-edit-note="canEditTask"
      :saving="isSavingTask"
      @close="closeTaskModal"
      @save="saveTask"
    />
  </span>
</template>

<script>
import {
  CREATE_SERVICE_VISIT_TASK_ITEM,
  REORDER_SERVICE_VISIT_TASK_ITEMS,
  UPDATE_SERVICE_VISIT_TASK_ITEM
} from "@/modules/service-visits/store/service-visits.module";
import ServiceVisitBoardTaskModal from "@/modules/service-visits/components/ServiceVisitBoardTaskModal.vue";

export default {
  name: "ServiceVisitBoardCardTasks",
  components: {
    ServiceVisitBoardTaskModal
  },
  props: {
    visitId: {
      type: Number,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    itemStatuses: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canManageTaskStructure: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      areTasksOpen: false,
      selectedTask: null,
      modalMode: "edit",
      isTaskModalOpen: false,
      isSavingTask: false,
      draggedTaskId: null,
      dragOverTaskId: null,
      suppressNextTaskClick: false,
      isReorderingTasks: false
    };
  },
  computed: {
    totalTaskCount() {
      return this.items.length;
    },
    tasks() {
      return this.sortedItems().map(item => this.taskViewModel(item));
    },
    canReorderTasks() {
      return this.canManageTaskStructure
        && !this.tasks.some(task => this.isTaskFinished(task))
        && this.tasks.length > 1
        && !this.isReorderingTasks;
    },
    canShowReorderHandles() {
      return this.canReorderTasks;
    },
    canEditTask() {
      if (this.modalMode === "create") {
        return this.canEdit;
      }

      return this.canEdit && this.selectedTask;
    }
  },
  methods: {
    trimTaskName(name) {
      const maxLength = 72;
      const value = name || "-";

      return value.length > maxLength ? `${value.substring(0, maxLength).trim()}...` : value;
    },
    statusColor(status) {
      return status && status.color ? status.color : "#3699ff";
    },
    statusName(status) {
      return status && status.name ? status.name : "-";
    },
    isTaskFinished(task) {
      return task && task.status && task.status.slug === "finished";
    },
    statusDotStyle(status) {
      return {
        backgroundColor: this.statusColor(status)
      };
    },
    sortedItems() {
      return [...this.items].sort((first, second) => {
        if (first.sortOrder === second.sortOrder) {
          return Number(first.id) - Number(second.id);
        }

        if (first.sortOrder === undefined || first.sortOrder === null) {
          return 1;
        }

        if (second.sortOrder === undefined || second.sortOrder === null) {
          return -1;
        }

        return Number(first.sortOrder) - Number(second.sortOrder);
      });
    },
    taskViewModel(item) {
      return {
        ...item,
        key: `service-visit-item-${item.id}`,
        name: item.name || "-",
        label: this.trimTaskName(item.name),
        status: item.status
      };
    },
    openTask(item) {
      if (this.suppressNextTaskClick) {
        this.suppressNextTaskClick = false;
        return;
      }

      this.selectedTask = item;
      this.modalMode = "edit";
      this.isTaskModalOpen = true;
    },
    openCreateTask() {
      this.selectedTask = null;
      this.modalMode = "create";
      this.isTaskModalOpen = true;
    },
    closeTaskModal() {
      if (this.isSavingTask) {
        return;
      }

      this.isTaskModalOpen = false;
      this.selectedTask = null;
      this.modalMode = "edit";
    },
    saveTask(payload) {
      this.isSavingTask = true;

      if (this.modalMode === "create") {
        this.createTask(payload);
        return;
      }

      this.updateTask(payload);
    },
    createTask(payload) {
      this.$store
        .dispatch(CREATE_SERVICE_VISIT_TASK_ITEM, {
          visitId: this.visitId,
          payload
        })
        .then(response => {
          this.$emit("task-created", {
            visitId: this.visitId,
            item: response.data
          });
          this.isTaskModalOpen = false;
          this.selectedTask = null;
          this.modalMode = "edit";
        })
        .catch(() => {
          this.showTaskUpdateFailedToast();
        })
        .finally(() => {
          this.isSavingTask = false;
        });
    },
    updateTask(payload) {
      if (!this.selectedTask) {
        this.isSavingTask = false;
        return;
      }

      this.$store
        .dispatch(UPDATE_SERVICE_VISIT_TASK_ITEM, {
          itemId: this.selectedTask.id,
          payload
        })
        .then(response => {
          this.$emit("task-updated", {
            visitId: this.visitId,
            item: response.data
          });
          this.isTaskModalOpen = false;
          this.selectedTask = null;
        })
        .catch(() => {
          this.showTaskUpdateFailedToast();
        })
        .finally(() => {
          this.isSavingTask = false;
        });
    },
    startTaskDrag(item, event) {
      if (!this.canReorderTasks) {
        return;
      }

      this.draggedTaskId = item.id;
      this.suppressNextTaskClick = true;

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(item.id));
      }
    },
    dropTask(item) {
      if (!this.draggedTaskId || this.draggedTaskId === item.id) {
        this.finishTaskDrag();
        return;
      }

      const reorderedTaskIds = this.reorderedTaskIds(this.draggedTaskId, item.id);

      if (!reorderedTaskIds.length) {
        this.finishTaskDrag();
        return;
      }

      this.isReorderingTasks = true;

      this.$store
        .dispatch(REORDER_SERVICE_VISIT_TASK_ITEMS, {
          visitId: this.visitId,
          payload: {
            itemIds: reorderedTaskIds
          }
        })
        .then(response => {
          this.$emit("tasks-reordered", {
            visitId: this.visitId,
            items: response.data
          });
        })
        .catch(() => {
          this.showTaskUpdateFailedToast();
        })
        .finally(() => {
          this.isReorderingTasks = false;
          this.finishTaskDrag();
        });
    },
    markTaskDropTarget(item) {
      if (!this.draggedTaskId || this.draggedTaskId === item.id) {
        this.dragOverTaskId = null;
        return;
      }

      this.dragOverTaskId = item.id;
    },
    clearTaskDropTarget(item) {
      if (this.dragOverTaskId === item.id) {
        this.dragOverTaskId = null;
      }
    },
    finishTaskDrag() {
      this.draggedTaskId = null;
      this.dragOverTaskId = null;

      window.setTimeout(() => {
        this.suppressNextTaskClick = false;
      }, 100);
    },
    reorderedTaskIds(draggedTaskId, targetTaskId) {
      const taskIds = this.tasks.map(task => task.id);
      const draggedIndex = taskIds.indexOf(draggedTaskId);
      const targetIndex = taskIds.indexOf(targetTaskId);

      if (draggedIndex < 0 || targetIndex < 0) {
        return [];
      }

      const reorderedTaskIds = taskIds.slice();
      const [draggedId] = reorderedTaskIds.splice(draggedIndex, 1);
      reorderedTaskIds.splice(targetIndex, 0, draggedId);

      return reorderedTaskIds;
    },
    showTaskUpdateFailedToast() {
      if (!this.$notify) {
        return;
      }

      this.$notify({
        group: "notify",
        type: "error",
        title: `<i class='flaticon-close'></i> ${this.$t("ALERTS.WARNING")}`,
        text: this.$t("SERVICE_VISITS.TASK_UPDATE_FAILED")
      });
    }
  }
};
</script>

<style scoped>
.service-visit-task-status-dot {
  height: 8px;
  width: 8px;
}

.cursor-pointer {
  cursor: pointer;
}

.service-visit-task-row--draggable {
  cursor: grab;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.service-visit-task-row--draggable:active {
  cursor: grabbing;
}

.service-visit-task-row--reorder-disabled {
  cursor: wait;
}

.service-visit-task-row--dragging {
  background-color: #f3f6f9;
  box-shadow: inset 3px 0 0 #3699ff;
}

.service-visit-task-row--drop-target {
  background-color: #eef7ff;
  box-shadow: inset 3px 0 0 #3699ff;
}

.service-visit-task-row__handle {
  flex: 0 0 auto;
}
</style>
